import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { TeamOutlined, BuildOutlined, BankOutlined } from '@ant-design/icons';
import { createModuleRouteGuard } from '@/modules/routeGuards';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { TabbedCountCard } from '@/components/shared/TabbedCountCard';
import { useCvManagerJobsListQuery, useCvManagerOrganizationsListQuery } from '../api';
import { ApplicationsTable, JobsTable, OrganizationsTable } from '../components';

export type CvManagerListSearch = {
  candidate_id?: number;
  include_archived?: 'all' | 'true';
};

function parseCvManagerListSearch(search: Record<string, unknown>): CvManagerListSearch {
  const rawId = search.candidate_id;
  const parsedId =
    typeof rawId === 'number'
      ? rawId
      : typeof rawId === 'string' && rawId.trim() !== ''
        ? Number(rawId)
        : Number.NaN;
  const includeArchived =
    search.include_archived === 'all' || search.include_archived === 'true'
      ? search.include_archived
      : undefined;

  return {
    ...(Number.isFinite(parsedId) ? { candidate_id: parsedId } : {}),
    ...(includeArchived ? { include_archived: includeArchived } : {}),
  };
}

export const Route = createFileRoute('/manage/modules/cvManager/')({
  component: CvManagerPage,
  validateSearch: parseCvManagerListSearch,
  beforeLoad: createModuleRouteGuard('cv-manager-module'),
});

function CvManagerPage() {
  const [activeTab, setActiveTab] = useState('applications');
  const [applicationsCount, setApplicationsCount] = useState(0);
  const [organizationsCount, setOrganizationsCount] = useState(0);
  const { t } = useTranslation('cv-manager-module');
  const { candidate_id: candidateId } = Route.useSearch();

  useEffect(() => {
    if (candidateId != null) setActiveTab('applications');
  }, [candidateId]);

  // Fetch job count even when Jobs tab isn’t mounted yet.
  const { data: jobsCountData } = useCvManagerJobsListQuery({ page: 1, page_size: 10, ordering: '-created_at' });
  const { data: orgsCountData } = useCvManagerOrganizationsListQuery({
    page: 1,
    page_size: 1,
    include_inactive: true,
  });

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader icon={<TeamOutlined />} title={t('pageTitle')} description={t('pageDescription')} />

      <TabbedCountCard
        activeKey={activeTab}
        onChange={setActiveTab}
        size="large"
        items={[
          {
            key: 'applications',
            icon: <TeamOutlined />,
            label: t('tabs.applications'),
            count: applicationsCount,
            children: <ApplicationsTable onTotalCountChange={setApplicationsCount} />,
          },
          {
            key: 'jobs',
            icon: <BuildOutlined />,
            label: t('tabs.jobs'),
            count: jobsCountData?.count ?? 0,
            children: <JobsTable />,
          },
          {
            key: 'organizations',
            icon: <BankOutlined />,
            label: t('tabs.organizations', { defaultValue: 'Organizations' }),
            count: organizationsCount || (orgsCountData?.count ?? 0),
            children: <OrganizationsTable onTotalCountChange={setOrganizationsCount} />,
          },
        ]}
      />
    </PageTransition>
  );
}
